import React, { useContext, useId, useState } from "react";
import { Socket } from "socket.io-client";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import OwButton from "../../shared/OwButton/OwButton";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwDateInput from "../../shared/OwDateInput/OwDateInput";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import Moment from "moment";

import IUserUI from "../../../models/IUserUI";
import ISupplierModel from "../../../models/ISupplierModel";
import QboReprocessOrderAssignments from "./QboReprocessOrderAssignments";
import UiContext from "../../../contexts/UiContext";
import "./QboPullInvoicesAsOrdersModal.scss";

type Props = {
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    cancelFn: (refreshData?: boolean) => void;
    startFn: (startDate: string, endDate?: string, docNumber?: string) => void;
    hasDefaultPriceGroup: boolean;
};

export const QboPullInvoicesAsOrdersModal = (props: Props) => {
    const calendarPortalId = useId();
    const uiContext = useContext(UiContext);
    const showReprocess = uiContext.showHiddenFeatures;
    const [selectedStartDate, setSelectedStartDate] = useState<Moment.Moment | null | undefined>();
    const [selectedEndDate, setSelectedEndDate] = useState<Moment.Moment | null | undefined>();
    const [reprocessing, setReprocessing] = useState(false);
    const connected = !!props.supplierModel.Supplier_Integration?.has_qbo;
    const [docNumber, setDocNumber] = useState<string>("");

    const start = () => {
        if (selectedStartDate && props.hasDefaultPriceGroup && connected && !reprocessing) {
            props.startFn(
                selectedStartDate.format("YYYY-MM-DD"),
                selectedEndDate ? selectedEndDate.format("YYYY-MM-DD") : undefined,
                docNumber.trim() ? docNumber.trim() : undefined
            );
            props.cancelFn(false);
        }
    };

    return (
        <>
            <div className={"QboPullInvoicesAsOrdersModal p-3"}>
                <div className={"d-flex justify-content-between"}>
                    <OwTitle tag={"h4"} title={"QuickBooks Online Pull Invoices"} disableTranslation={true} />
                    <div>
                        <OwButton
                            disabled={reprocessing}
                            className={"btn-danger"}
                            centerIconClass={"fas fa-times"}
                            fn={() => {
                                props.cancelFn(false);
                            }}
                        />
                    </div>
                </div>
                {!connected && (
                    <div className="alert alert-warning mt-2 mb-0">
                        Connect QuickBooks Online to import invoices.
                        {showReprocess ? " You can still reprocess imported order assignments below." : ""}
                    </div>
                )}
                {!props.hasDefaultPriceGroup && (
                    <div className={"alert alert-warning mt-2 mb-0"}>
                        <strong>Default Price Group required</strong> - You must select a Default Price Group on your QuickBooks Online
                        integration before you can pull invoices as orders.
                    </div>
                )}
                <fieldset disabled={reprocessing} className={"border-0 p-0 mx-0 mt-2 mb-0"}>
                    <div className={"row"}>
                        <div className={"col-12 col-md-6 mt-2"}>
                            <div className={"d-flex align-items-center"}>
                                <OwTitle tag={"h6"} title={"start_date"} defaultMargins={false} />
                                <span className="text-muted ms-1 small">(required for import)</span>
                            </div>
                            <OwDateInput
                                portalId={calendarPortalId}
                                selectedDate={selectedStartDate}
                                fn={(date) => {
                                    setSelectedStartDate(date);
                                }}
                            />
                        </div>
                        <div className={"col-12 col-md-6 mt-2"}>
                            <div className={"d-flex align-items-center"}>
                                <OwTitle tag={"h6"} title={"end_date"} defaultMargins={false} />
                            </div>
                            <OwDateInput
                                portalId={calendarPortalId}
                                selectedDate={selectedEndDate}
                                fn={(date) => {
                                    setSelectedEndDate(date);
                                }}
                            />
                        </div>
                    </div>
                    <div className={"mt-2"}>
                        <OwTitle tag={"h6"} title={"Doc. Number"} disableTranslation={true} />
                        <OwFnInput
                            name={"docNumber"}
                            value={docNumber}
                            placeholder={"Doc. Number"}
                            skipTranslate={true}
                            fn={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setDocNumber(e.target.value);
                            }}
                        />
                    </div>
                </fieldset>
                <div className={"mt-2"}>
                    <OwButton
                        title={"start"}
                        fn={start}
                        disabled={!selectedStartDate || !props.hasDefaultPriceGroup || !connected || reprocessing}
                    />
                </div>
                {showReprocess && (
                    <QboReprocessOrderAssignments
                        userModel={props.userModel}
                        supplierModel={props.supplierModel}
                        notifyFn={props.notifyFn}
                        startDate={selectedStartDate ? selectedStartDate.format("YYYY-MM-DD") : ""}
                        endDate={selectedEndDate ? selectedEndDate.format("YYYY-MM-DD") : ""}
                        docNumber={docNumber}
                        onLockedChange={setReprocessing}
                    />
                )}
            </div>
            <div id={calendarPortalId} className="qbo-invoice-calendar-portal" />
        </>
    );
};
